import { type Dispatch, type SetStateAction } from 'react'

import type { CustomListProjection } from '@api'
import type { ColumnDef, PaginationState } from '@tanstack/react-table'
import { format } from 'date-fns'

import { DataTable } from '@/components/ui/table/controlled-pagination/DataTable'
import { DIALOG_TYPE } from '@/dialogs/constants'
import { openDialog } from '@/models/dialogs'

import { useCustomListMutations } from '../hooks/useCustomListMutations'
import { CustomListTableActions } from './CustomListTableActions'

interface Props {
  data?: CustomListProjection[]
  isFetching: boolean
  paginationState: PaginationState
  onPaginationChange: Dispatch<SetStateAction<PaginationState>>
  pageCount: number
}

export const CustomListsTable = ({
  data = [],
  isFetching,
  paginationState,
  onPaginationChange,
  pageCount,
}: Props) => {
  const { deleteCustomListMutation, editCustomListMutation } =
    useCustomListMutations()
  const handleViewFans = (list: CustomListProjection) => {
    openDialog({
      type: DIALOG_TYPE.CUSTOM_LIST_VIEW_FANS,
      listId: list.id!,
    })
  }

  const handleEdit = (list: CustomListProjection) => {
    openDialog({
      type: DIALOG_TYPE.CUSTOM_LIST_FORM,
      title: 'Edit List',
      testId: 'edit-custom-list-dialog',
      initialValues: list,
      submitLabel: 'Save',
      onSubmit: (values) => {
        return editCustomListMutation.mutateAsync({
          listId: list.id!,
          payload: values,
        })
      },
    })
  }

  const handleDelete = (list: CustomListProjection) => {
    openDialog({
      type: DIALOG_TYPE.CUSTOM_LIST_DELETE,
      onSubmit: () => {
        return deleteCustomListMutation.mutateAsync(list.id!)
      },
      listId: list.id!,
    })
  }

  const columns: ColumnDef<CustomListProjection>[] = [
    {
      accessorKey: 'name',
      header: 'List Name',
    },
    {
      accessorKey: 'userCount',
      header: 'Amount of Fans',
      cell: (ctx) => {
        return ctx.row.original.fansSize ?? '-'
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Created At',
      cell: (ctx) => {
        return ctx.row.original.createdAt
          ? format(ctx.row.original.createdAt, 'LLL dd, y hh:mm a')
          : '-'
      },
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      cell: (ctx) => {
        return (
          <CustomListTableActions
            row={ctx.row.original}
            onViewFans={handleViewFans}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        )
      },
      meta: {
        isActionColumn: true,
      },
    },
  ]

  return (
    <DataTable
      columns={columns}
      data={data}
      enablePagination
      paginationState={paginationState}
      onPaginationChange={onPaginationChange}
      isLoading={isFetching}
      pageCount={pageCount}
      tbodyMinHeight={false}
      testId="custom-lists-table"
    />
  )
}
